import { marketplaceService } from '../services/marketplace';
import type { OperatorReturnIntent, ReturnIntentStatus } from '../types/marketplace';
import { toErrorMessage, type MarketplaceSet } from './marketplaceStoreTypes';

export function createReturnIntentSlice(set: MarketplaceSet) {
    return {
        activeReturnIntent: null as OperatorReturnIntent | null,
        returnIntentLoaded: false,
        isLoadingReturnIntent: false,
        isSavingReturnIntent: false,
        returnStatus: null as ReturnIntentStatus | null,
        returnStatusLoadedProviderId: null as number | null,

        fetchReturnStatus: async (providerId: number | null = null): Promise<ReturnIntentStatus | null> => {
            set({ isLoadingReturnIntent: true, error: null });

            try {
                const response = await marketplaceService.getReturnIntentStatus();
                set({
                    returnStatus: response.data,
                    returnStatusLoadedProviderId: providerId ?? response.data.provider.id,
                    returnIntentLoaded: true,
                    isLoadingReturnIntent: false,
                });
                return response.data;
            } catch (error) {
                set({
                    returnStatus: null,
                    returnStatusLoadedProviderId: providerId,
                    returnIntentLoaded: true,
                    isLoadingReturnIntent: false,
                    error: toErrorMessage(error, 'No se pudo cargar Regreso Pagado Seguro.'),
                });
                return null;
            }
        },

        fetchActiveReturnIntent: async (): Promise<OperatorReturnIntent | null> => {
            set({ isLoadingReturnIntent: true, error: null });

            try {
                const response = await marketplaceService.getActiveReturnIntent();
                const intent = response.data;

                set({
                    activeReturnIntent: intent,
                    returnIntentLoaded: true,
                    isLoadingReturnIntent: false,
                });

                return intent;
            } catch (error) {
                set({
                    isLoadingReturnIntent: false,
                    returnIntentLoaded: true,
                    error: toErrorMessage(error, 'No se pudo cargar Regreso Pagado Seguro.'),
                });
                return null;
            }
        },

        activateReturnIntent: async (
            payload: Parameters<typeof marketplaceService.activateReturnIntent>[0]
        ): Promise<OperatorReturnIntent | null> => {
            set({ isSavingReturnIntent: true, error: null });

            try {
                const response = await marketplaceService.activateReturnIntent(payload);

                set({
                    activeReturnIntent: response.data,
                    returnIntentLoaded: true,
                    isSavingReturnIntent: false,
                });

                return response.data;
            } catch (error) {
                set({
                    isSavingReturnIntent: false,
                    error: toErrorMessage(error, 'No se pudo activar Regreso Pagado Seguro.'),
                });
                return null;
            }
        },

        cancelReturnIntent: async (intentId: number): Promise<boolean> => {
            set({ isSavingReturnIntent: true, error: null });

            try {
                await marketplaceService.cancelReturnIntent(intentId);

                set({
                    activeReturnIntent: null,
                    returnIntentLoaded: true,
                    isSavingReturnIntent: false,
                });

                return true;
            } catch (error) {
                set({
                    isSavingReturnIntent: false,
                    error: toErrorMessage(error, 'No se pudo cancelar Regreso Pagado Seguro.'),
                });
                return false;
            }
        },

        saveHomeBase: async (payload: Parameters<typeof marketplaceService.saveHomeBase>[0]): Promise<boolean> => {
            set({ isSavingReturnIntent: true, error: null });

            try {
                const response = await marketplaceService.saveHomeBase(payload);
                const homeBase = response.data;

                set((state) => ({
                    operatorProfile: state.operatorProfile
                        ? {
                            ...state.operatorProfile,
                            ...homeBase,
                            has_home_base: homeBase.home_base_lat !== null && homeBase.home_base_lng !== null,
                        }
                        : state.operatorProfile,
                    isSavingReturnIntent: false,
                }));

                return true;
            } catch (error) {
                set({
                    isSavingReturnIntent: false,
                    error: toErrorMessage(error, 'No se pudo guardar la base de regreso.'),
                });
                return false;
            }
        },

        clearHomeBase: async (): Promise<boolean> => {
            set({ isSavingReturnIntent: true, error: null });

            try {
                await marketplaceService.clearHomeBase();

                set((state) => ({
                    operatorProfile: state.operatorProfile
                        ? {
                            ...state.operatorProfile,
                            home_base_lat: null,
                            home_base_lng: null,
                            home_base_label: null,
                            home_base_address: null,
                            has_home_base: false,
                        }
                        : state.operatorProfile,
                    isSavingReturnIntent: false,
                }));

                return true;
            } catch (error) {
                set({
                    isSavingReturnIntent: false,
                    error: toErrorMessage(error, 'No se pudo borrar la base de regreso.'),
                });
                return false;
            }
        },
    };
}
